/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import React, {useCallback} from "react";
import {useDispatch} from "react-redux";
import {ScatterPlot} from "../ScatterPlot";
import {IScatterPlotStepProps} from "../../../../../components/Wizard/types";
import {
  useSelectCosmosData,
  useSelectErrorMessageList,
  useSelectFileUploadResponse,
  useSelectWizardMetaData,
  useSliceActions
} from "../../../../../../redux/ducks/Wizard/SliceProvider";
import TemplateStep from "../../../../../components/Wizard/Steps/TemplateStep";
import {useMsal} from "@azure/msal-react";
import {checkResponse} from "../../../../../../auth/authConfig";
import toast from "react-hot-toast";

import {Grid, Typography} from '@mui/material';
import {usePostRequest} from "../../../../../../api/helper";
import {addRadarEndpoint} from "../../constants";

const ReviewPerformancePageTitle = (props: { sensorName: string }) => {
  const {sensorName} = props
  return (
    <Grid container justifyContent={'center'}>
      <Typography variant='h6' component='div' whiteSpace={'normal'} textAlign={'center'}>
        Review System Component Performance for Radar
        Sensor ({sensorName ?? 'N/A'})
      </Typography>
    </Grid>
  )
}

export const StepPlot = (props: IScatterPlotStepProps) => {
  const {id, isLastWizardStep, is3d, messagesTitle} = props

  const stepIndex = isLastWizardStep ? 3 : 2
  /**
   * use error messages length for whether or not there is error for this data sheet
   */

  const errorMessages = useSelectErrorMessageList(stepIndex)
  const isDataSheetInvalid = errorMessages?.length > 0
  const cosmosData = useSelectCosmosData()
  const fileSheetNames: string[] = useSelectFileUploadResponse()?.sheet_names
  const sensorSheetName: string = isLastWizardStep ? fileSheetNames?.[2] : fileSheetNames?.[1]
  const xyzData = useSelectFileUploadResponse()?.plotData?.[sensorSheetName]?.data
  const wizardMetaData = useSelectWizardMetaData()
  const dispatch = useDispatch()
  const {appendStepXErrorMessages, setWizardOpen, clearState, refreshTable} = useSliceActions()

  const addSensorHandle = usePostRequest(addRadarEndpoint)
  const handleAddSensor = async () => {
    const supplier = cosmosData.instructions_data.supplier.replace("/", "_")
    const component = cosmosData.instructions_data.component.replace("/", "_")
    const version = cosmosData.instructions_data.componentVersion
    const id = wizardMetaData?.id
    let _cosmosData = {
      "supplier": supplier, 
      "component" : component,
      "version" : version, 
      "id" : id
    }
    let reqBodyData: any = {
      "cosmos_data" : _cosmosData, 
    }
    if (id != undefined) {
      reqBodyData["id"] = id
    }
    
    addSensorHandle(reqBodyData)
      .then(_response => {
        dispatch(refreshTable())
        dispatch(setWizardOpen(false))
        dispatch(clearState())
      })
      .catch(error => {
        const errorMessage = error.statusText + ", please try again or contact support for assistance. Error on Sensor Radar Wizard Submission."
        toast.error(errorMessage)
        const msg = {text: errorMessage, color: '#FF0000'}
        dispatch(appendStepXErrorMessages([msg], stepIndex))
      })
  }

  const isSubmitDisabled = useCallback(() => {
    return cosmosData == null
  }, [cosmosData])

  const content2 = (
    <div>
      {/* row 1 - title */}
      <ReviewPerformancePageTitle sensorName={sensorSheetName}/>

      {/* row 2 - plot (data table if error) */}
      <ScatterPlot xyzData={xyzData} isError={isDataSheetInvalid} is3d={is3d}/>
    </div>
  )
  return (
    <TemplateStep
      content1={null} content2={content2} errorMessages={errorMessages} messagesTitle={messagesTitle}
      isFirstWizardStep={false} isLastWizardStep={isLastWizardStep ?? false} customTopRow={false}
      isSubmitDisabled={isSubmitDisabled}
      handleSubmit={handleAddSensor} submitPopupEnabled={true} popupTitle={'Add Sensor Radar'}
      submitDialogContentText={'Would you like to add this sensor?'}/>
  );
}

